import { useState } from 'react';
import {
  ActionsDialogue, Bouton, Dialogue, GrilleFormulaire, Message, Selecteur,
} from '@journal/ui';
import { useDonnees, useEcriture } from '../lib/donnees.ts';
import { useSession } from '../lib/session.tsx';
import type { RentreeDisponible } from '../pages/Reglages.tsx';
import { NIVEAUX_DISPONIBLES, basculerNiveau } from './ReglagesGeneral.tsx';
import { LienBouton } from './LienBouton.tsx';

/**
 * Le paramétrage minimal, demandé à la première ouverture.
 *
 * ── Ce qui bloquait ─────────────────────────────────────────────────────────
 * Sans année scolaire active, la moitié de l'application répond en erreur.
 * L'accueil le disait déjà et renvoyait vers les Réglages — mais il taisait la
 * ZONE de vacances, dont dépendent les dates des périodes. Une année créée
 * sans elle naît sans découpage : la programmation n'affiche qu'une colonne,
 * la progression aucune semaine, et rien n'explique pourquoi. Constaté en
 * montant une école de bout en bout.
 *
 * Deux questions, donc, posées ensemble et une seule fois — l'ordre compte,
 * la zone datant les périodes que la création pose.
 *
 * ── Pourquoi une boîte et non un écran de plus ──────────────────────────────
 * On ne la traverse qu'une fois. Un écran d'accueil dédié resterait dans le
 * code et dans la navigation longtemps après avoir cessé de servir ; la boîte,
 * elle, ne s'ouvre que tant que la condition tient.
 */
export function PremierParametrage({
  peutAdministrer, estEcole, surTermine,
}: {
  /** Un enseignant ne crée pas l'année de son école : il l'attend. */
  peutAdministrer: boolean;
  /**
   * L'espace est-il une ÉCOLE ?
   *
   * Ce qui reste à faire après l'année n'est pas le même : une école répartit
   * des classes entre des collègues qui n'ont pas encore de compte ; un
   * enseignant seul a déjà la sienne, créée d'office, et n'a qu'à dire ce
   * qu'il enseigne.
   */
  estEcole: boolean;
  surTermine: () => void;
}) {
  const { api } = useSession();
  const { executer, enCours, erreur } = useEcriture();
  /*
   * La liste suit la ZONE choisie ici, pas celle enregistrée.
   *
   * Un calendrier n'existe que par zone. Au premier paramétrage l'école n'en a
   * pas encore, le serveur repliait donc sur B — et une école de zone A ne
   * voyait jamais sa propre rentrée, sans que rien ne l'explique.
   */
  const [zone, setZone] = useState('B');
  const disponibles = useDonnees<RentreeDisponible[]>(
    `/school-years/available?zone=${zone}`, [zone],
  );
  const classes = useDonnees<Array<{ id: string; levels: string }>>('/classrooms');

  const [rentree, setRentree] = useState('');
  const [niveaux, setNiveaux] = useState<string[]>([]);
  /** Vrai une fois l'année ouverte : l'école a encore du chemin, pas le prof seul. */
  const [anneeOuverte, setAnneeOuverte] = useState(false);

  const liste = disponibles.donnees ?? [];
  /*
   * Par défaut, la rentrée EN COURS — pas la première de la liste.
   *
   * Le serveur les rend de la plus ancienne à la plus récente : une école qui
   * s'inscrit en septembre 2026 se voyait proposer 2024-2025, et l'acceptait
   * sans y regarder. Tout l'atterrissait alors deux ans en arrière — périodes,
   * programme, calendrier.
   *
   * Celle qui couvre aujourd'hui, sinon la plus récente : une inscription
   * pendant l'été vise la rentrée qui vient, pas celle qui s'achève.
   */
  const aujourdhui = new Date().toISOString().slice(0, 10);
  const parDefaut = liste.find((r) => r.startsOn <= aujourdhui && aujourdhui <= r.endsOn)
    ?? liste[liste.length - 1];
  const choisie = liste.find((r) => r.label === rentree) ?? parDefaut;

  async function demarrer() {
    if (!choisie) return;
    const ok = await executer(async () => {
      /*
       * La zone AVANT l'année : c'est elle qui date les cinq périodes que la
       * création pose. Dans l'autre ordre, l'année naît sans découpage et il
       * faut la redater à la main.
       */
      await api.requete('/settings/zone', { methode: 'PUT', corps: { value: zone } });
      const creee = await api.requete<{ id: string }>('/school-years', {
        methode: 'POST',
        // La rentrée seule : libellé et dates sont lus au calendrier officiel.
        corps: { year: choisie.year },
      });
      // Créer n'active pas : tous les modules lisent l'année ACTIVE, et une
      // année créée sans être activée laisse l'application dans l'état qu'on
      // vient précisément de quitter.
      await api.requete(`/school-years/${creee.id}/activate`, { methode: 'PUT' });

      /*
       * Le NIVEAU de la classe, pour un enseignant seul.
       *
       * Sa classe est créée d'office par le serveur — il n'a personne pour la
       * lui confier — mais elle naît sans niveau. Tant qu'il n'est pas
       * renseigné, le programme ne se filtre pas et les matières remontent
       * vides : c'est le dernier réglage sans lequel rien ne s'affiche.
       *
       * Une école, elle, compose ses classes une à une : la question n'a pas
       * de réponse unique, et se pose ailleurs.
       */
      const sienne = classes.donnees?.[0];
      if (!estEcole && sienne && niveaux.length > 0) {
        await api.requete(`/classrooms/${sienne.id}`, {
          methode: 'PATCH',
          corps: { levels: niveaux.join(',') },
        });
      }
    });
    if (!ok) return;
    // Une école n'en a pas fini : elle doit encore ouvrir des comptes et
    // répartir ses classes. On le lui dit maintenant, pas dans trois écrans.
    if (estEcole) setAnneeOuverte(true);
    else surTermine();
  }

  if (anneeOuverte) {
    return (
      <Dialogue
        ouvert
        surFermeture={surTermine}
        titre="Votre année est ouverte. Voici la suite."
        description="Trois gestes, et chaque enseignant retrouve sa classe."
      >
        <div className="flex flex-col gap-4 text-sm">
          <Message ton="succes">
            L’année scolaire, ses cinq périodes et les matières du programme
            sont en place. Elles valent pour toute l’école : personne n’aura à
            les refaire.
          </Message>

          {/*
            * L'ORDRE compte, et il n'est pas devinable.
            *
            * Créer un compte avant la classe, c'est y revenir pour l'affecter.
            * Créer la classe sans niveau, c'est un programme vide et des
            * écrans qui paraissent en panne. On énonce donc la suite dans
            * l'ordre où elle se fait, une fois pour toutes.
            */}
          <ol className="flex list-decimal flex-col gap-3 pl-5">
            <li>
              <span className="font-medium">Créez vos classes</span>, avec leur
              niveau. C’est lui qui décide du programme : une classe sans niveau
              n’affiche aucune matière.
            </li>
            <li>
              <span className="font-medium">Ouvrez un compte par enseignant.</span>
              {' '}
              <em>Créer</em> un compte le pose tout de suite : vous pouvez lui
              affecter sa classe avant même qu’il se connecte, et il reçoit un
              lien pour choisir son mot de passe. <em>Inviter</em> sert quand
              l’adresse a déjà un compte ailleurs : on ne réquisitionne pas le
              compte de quelqu’un.
            </li>
            <li>
              <span className="font-medium">Confiez chaque classe à son titulaire.</span>
              {' '}
              Une classe sans enseignant n’est la classe de travail de personne :
              son cahier journal reste inaccessible.
            </li>
          </ol>

          <Message>
            Chaque enseignant garde ensuite la main sur ce qui lui appartient :
            son emploi du temps, son cahier journal, ses progressions, ses
            élèves. Vous ne les paramétrez pas à sa place.
          </Message>

          <ActionsDialogue>
            <Bouton variante="ghost" onClick={surTermine}>Plus tard</Bouton>
            {/*
              * Vers ÉQUIPE, où les classes se créent — et non vers les
              * Réglages, qui portent l'année, les périodes et les matières.
              * Un bouton qui dépose ailleurs qu'où il annonce fait chercher.
              *
              * Naviguer démonte l'accueil ; au retour, l'année existe et la
              * boîte ne se rouvre pas.
              */}
            <LienBouton vers="/equipe">Créer mes classes</LienBouton>
          </ActionsDialogue>
        </div>
      </Dialogue>
    );
  }

  if (!peutAdministrer) {
    return (
      <Dialogue
        ouvert
        surFermeture={surTermine}
        titre="Votre école n’a pas encore d’année scolaire"
      >
        <div className="flex flex-col gap-4">
          <Message>
            L’année scolaire, ses périodes et les matières sont réglées une
            seule fois pour toute l’école. Demandez à la direction de les
            ouvrir : vous retrouverez ensuite votre classe et votre cahier
            journal.
          </Message>
          {/*
            * Une sortie, même quand il n'y a rien à faire : un écran sans
            * bouton se lit comme une panne, et l'on cherche la croix qui
            * n'existe pas.
            */}
          <ActionsDialogue>
            <Bouton variante="ghost" onClick={surTermine}>Fermer</Bouton>
          </ActionsDialogue>
        </div>
      </Dialogue>
    );
  }

  return (
    <Dialogue
      ouvert
      /*
       * Refermer mène à l'écran d'accueil qui explique la même chose et renvoie
       * aux Réglages : la boîte guide, elle n'enferme pas.
       */
      surFermeture={surTermine}
      titre="Ouvrons votre année scolaire"
      description="Deux réponses, une seule fois. Tout le reste en découle."
    >
      <form
        onSubmit={(e) => { e.preventDefault(); void demarrer(); }}
        className="flex flex-col gap-4"
      >
        <Message>
          L’année scolaire, ses périodes et les matières valent pour toute
          l’école. Chaque enseignant y ajoute ensuite sa classe, son emploi du
          temps et son cahier journal.
        </Message>

        {liste.length === 0 ? (
          /*
           * Aucune rentrée publiée : l'école ne peut rien ouvrir, et ce n'est
           * pas de son fait. Le dire vaut mieux qu'un menu vide.
           */
          <Message ton="attention">
            Aucune rentrée n’est encore ouverte par la plateforme. Écrivez-nous
            si vous devez préparer votre année dès maintenant.
          </Message>
        ) : (
          <GrilleFormulaire>
            <Selecteur
              libelle="Année scolaire"
              value={choisie?.label ?? ''}
              onChange={(e) => setRentree(e.target.value)}
              options={liste.map((r) => ({ valeur: r.label, texte: r.label }))}
            />
            <Selecteur
              libelle="Zone de vacances"
              value={zone}
              onChange={(e) => setZone(e.target.value)}
              options={[
                { valeur: 'A', texte: 'Zone A' },
                { valeur: 'B', texte: 'Zone B' },
                { valeur: 'C', texte: 'Zone C' },
              ]}
              aide="Elle date les périodes sur le calendrier officiel."
            />
          </GrilleFormulaire>
        )}

        {/*
          * Le NIVEAU, pour un enseignant seul seulement.
          *
          * Sa classe est créée d'office par le serveur, mais sans niveau — et
          * c'est lui qui décide du programme : sans réponse, les matières
          * remontent vides et l'écran paraît en panne.
          *
          * Une école compose ses classes une à une : la question n'a pas de
          * réponse unique, et se pose au moment de créer chaque classe.
          *
          * Des BOUTONS, pas un champ texte : « ce2 » mal orthographié ne
          * correspondrait à aucun domaine du programme. Un double niveau — le
          * cas ordinaire en primaire — se compose en cochant deux boutons.
          */}
        {!estEcole && liste.length > 0 && (
          <div>
            <span className="mb-1.5 block text-[13px] font-medium">
              Niveau de votre classe
            </span>
            <div
              role="group"
              aria-label="Niveau de votre classe"
              className="grid grid-cols-5 gap-1.5 sm:grid-cols-9"
            >
              {NIVEAUX_DISPONIBLES.map((n) => {
                const actif = niveaux.includes(n);
                return (
                  <button
                    key={n}
                    type="button"
                    aria-pressed={actif}
                    onClick={() => setNiveaux(basculerNiveau(niveaux, n))}
                    className={[
                      'rounded-lg border px-2 py-1.5 text-[12px] font-medium',
                      'transition-colors duration-150 motion-reduce:transition-none',
                      'focus-visible:outline-2 focus-visible:outline-offset-1',
                      'focus-visible:outline-[var(--color-focus)]',
                      actif
                        ? 'border-[var(--color-primary)] bg-[var(--color-primary-soft)] text-[var(--color-primary-soft-fg)]'
                        : 'border-[var(--color-border)] hover:bg-[var(--color-surface-hover)]',
                    ].join(' ')}
                  >
                    {n}
                  </button>
                );
              })}
            </div>
            <p className="mt-1.5 text-xs leading-snug text-[var(--color-fg-muted)]">
              C’est lui qui décide du programme proposé. Modifiable ensuite dans
              les réglages.
            </p>
          </div>
        )}

        {erreur && <Message ton="erreur">{erreur}</Message>}

        <ActionsDialogue>
          <Bouton
            type="submit"
            variante="primary"
            chargement={enCours}
            disabled={!choisie}
          >
            Ouvrir l’année
          </Bouton>
        </ActionsDialogue>
      </form>
    </Dialogue>
  );
}
